import React from "react";
import { ImageGeneratorCatalogSurfaceSlide } from "../../app/api/imageGenerator";
import {
  FieldSelect,
  RequiredMark,
  helperTextClassName,
  sectionClassName,
  sectionTitleClassName,
} from "./ImageGeneratorControls";
import { reportSlidePageButtonClassName } from "./collectionBuildStyles";
import ImageGeneratorSelectionTick from "./ImageGeneratorSelectionTick";

type Props = {
  slides: ImageGeneratorCatalogSurfaceSlide[];
  page: number;
  selectedId: string;
  loading?: boolean;
  disabled?: boolean;
  onPageChange: (page: number) => void;
  onSelect: (id: string) => void;
};

/** Figma Slide Reports: outer shell hugs the slide; pagination aligns to that width. */
const ImageGeneratorSlideReports: React.FC<Props> = ({
  slides,
  page,
  selectedId,
  loading,
  disabled,
  onPageChange,
  onSelect,
}) => {
  const total = slides.length;
  const safePage = total > 0 ? Math.min(Math.max(page, 1), total) : 1;
  const current = total > 0 ? slides[safePage - 1] : null;
  const isSelected = Boolean(current && current.id === selectedId);
  const selectedIndex = selectedId
    ? slides.findIndex(slide => slide.id === selectedId)
    : -1;
  const selectedPage = selectedIndex >= 0 ? selectedIndex + 1 : 0;
  const browsingOther =
    Boolean(selectedId) && selectedPage > 0 && selectedPage !== safePage;
  const url =
    current?.image?.available && current.image.url ? current.image.url : "";

  return (
    <div className={sectionClassName}>
      <h2 className={`${sectionTitleClassName} mb-4`}>
        Slide Reports
        <RequiredMark />
      </h2>

      {loading ? (
        <p className={helperTextClassName}>Loading slide reports…</p>
      ) : total === 0 ? (
        <p className={helperTextClassName}>
          No surface-report slides for these filters.
        </p>
      ) : (
        <div className="w-full max-w-full text-left">
          <div className="inline-block max-w-full">
            <button
              type="button"
              disabled={disabled || !current}
              aria-pressed={isSelected}
              title={
                isSelected
                  ? current?.name || "Selected surface slide"
                  : current?.name
                    ? `Select ${current.name}`
                    : "Select surface slide"
              }
              onClick={() => {
                if (!current || disabled) {
                  return;
                }
                // Always select the visible slide (browsing keeps prior selection until click).
                onSelect(current.id);
              }}
              className={`relative inline-block max-w-full overflow-hidden rounded-[20px] border bg-transparent p-0 leading-none outline-none transition-all focus-visible:outline-none ${
                isSelected
                  ? "border-2 border-[#737373]"
                  : "border border-[#737373]/50 hover:border-[#737373]"
              } disabled:cursor-default`}
            >
              {url ? (
                <img
                  src={url}
                  alt={current?.name || "Surface slide"}
                  className="block h-auto w-auto max-h-[460px] max-w-full object-contain sm:max-h-[560px]"
                  draggable={false}
                />
              ) : (
                <span className="flex h-[240px] w-[min(100%,640px)] items-center justify-center bg-[#F3F3F3] px-4 text-sm text-tertiary sm:h-[300px]">
                  {current?.name || "Slide"}
                </span>
              )}
              {isSelected ? (
                <ImageGeneratorSelectionTick className="left-3 top-3" />
              ) : null}
            </button>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-secondary">
              <p className="min-w-0 text-left text-secondary">
                {browsingOther
                  ? `Slide ${selectedPage} selected — browsing ${safePage} of ${total}. Click to select this one.`
                  : selectedId
                    ? `Slide ${selectedPage || safePage} selected.`
                    : "Click a slide to select it, then browse others anytime."}
              </p>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onPageChange(Math.max(1, safePage - 1))}
                  disabled={safePage <= 1}
                  className={reportSlidePageButtonClassName}
                  aria-label="Previous slide report"
                >
                  &lt;
                </button>
                <div className="w-[72px]">
                  <FieldSelect
                    value={String(safePage)}
                    onChange={next => onPageChange(Number(next))}
                    options={Array.from({ length: total }, (_, index) => ({
                      value: String(index + 1),
                      label: String(index + 1),
                    }))}
                  />
                </div>
                <span>of {total}</span>
                <button
                  type="button"
                  onClick={() => onPageChange(Math.min(total, safePage + 1))}
                  disabled={safePage >= total}
                  className={reportSlidePageButtonClassName}
                  aria-label="Next slide report"
                >
                  &gt;
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGeneratorSlideReports;
